import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getUserProfile, type PublicProfile } from "../lib/auth-api";
import type { RoomAdapter } from "../lib/auth-api";
import { useI18n } from "../lib/i18n";
import type { Peer } from "../lib/use-room";
import type { AuthUser } from "../lib/auth-api";
import { AgentQuotaBar } from "./AgentQuotaBar";
import { Avatar } from "./Avatar";

interface RoomMembersPanelProps {
  peers: Peer[];
  selfDevice: string;
  user: AuthUser | null;
  adapters?: RoomAdapter[];
  onSelectAgent?: (adapterId: string) => void;
}

interface OnlineUser {
  key: string;
  id: string;
  label: string;
  sublabel: string;
  avatarUrl: string | null;
  kind: "human" | "agent";
  isSelf?: boolean;
}

export function RoomMembersPanel({
  peers,
  selfDevice,
  user,
  adapters = [],
  onSelectAgent,
}: RoomMembersPanelProps) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [profiles, setProfiles] = useState<Record<string, PublicProfile>>({});

  useEffect(() => {
    let cancelled = false;
    const targets = peers
      .map((p) => p.device_id)
      .filter((d) => d.startsWith("usr_") && !profiles[d]);
    if (targets.length === 0) return;
    (async () => {
      const results = await Promise.all(targets.map((id) => getUserProfile(id)));
      if (cancelled) return;
      setProfiles((prev) => {
        const next = { ...prev };
        results.forEach((p, i) => {
          if (p) next[targets[i]] = p;
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [peers, profiles]);

  const members = useMemo<OnlineUser[]>(() => {
    const list: OnlineUser[] = [
      {
        key: "self",
        id: user?.id ?? selfDevice,
        label: user?.display_name || user?.username || user?.email || t("members.self"),
        sublabel: t("members.self"),
        avatarUrl: user?.avatar_url ?? null,
        kind: "human",
        isSelf: true,
      },
    ];
    for (const a of adapters) {
      list.push({
        key: `agent-${a.id}`,
        id: a.id,
        label: a.id,
        sublabel: a.description || t("members.agent.sub"),
        avatarUrl: null,
        kind: "agent",
      });
    }
    for (const p of peers) {
      const pf = profiles[p.device_id];
      list.push({
        key: `peer-${p.conn_id}`,
        id: pf?.id ?? p.device_id,
        label:
          pf?.display_name ||
          pf?.username ||
          (p.device_id.startsWith("anon-") ? t("members.anon") : p.device_id.slice(0, 10)),
        sublabel: p.device_id.startsWith("anon-") ? p.device_id : t("members.online"),
        avatarUrl: pf?.avatar_url ?? null,
        kind: "human",
      });
    }
    return list;
  }, [adapters, peers, profiles, selfDevice, user, t]);

  function openDirect(member: OnlineUser) {
    if (member.isSelf) return;
    if (member.kind === "agent") {
      onSelectAgent?.(member.id);
      return;
    }
    const selfId = user?.id ?? selfDevice;
    const roomId = directRoomId(selfId, member.id);
    nav(`/rooms/${roomId}`);
  }

  return (
    <aside className="room-members" aria-label={t("members.title")}>
      <header className="room-members__head">
        <span>{t("members.title")}</span>
        <strong>{members.length}</strong>
      </header>
      <div className="room-members__list">
        {members.map((m) => (
          <button
            key={m.key}
            type="button"
            className={[
              "room-member",
              m.isSelf ? "room-member--self" : "",
              m.kind === "agent" ? "room-member--agent" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            disabled={m.isSelf}
            onClick={() => openDirect(m)}
            title={
              m.isSelf
                ? "当前账号"
                : m.kind === "agent"
                  ? `打开 @${m.label} 终端会话`
                  : `和 ${m.label} 单独聊天`
            }
          >
            <Avatar seed={m.id || m.label} label={m.label} size={32} src={m.avatarUrl} />
            <span className="room-member__body">
              <span className="room-member__name">{m.label}</span>
              <span className="room-member__sub">{m.isSelf ? "当前账号" : m.sublabel}</span>
              {m.kind === "agent" && <AgentQuotaBar adapterId={m.id} />}
            </span>
          </button>
        ))}
      </div>
    </aside>
  );
}

function directRoomId(a: string, b: string): string {
  return `dm-${[stableId(a), stableId(b)].sort().join("-")}`;
}

function stableId(id: string): string {
  return id.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
}
